import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Input, SimpleFormField } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';

import { ApiError, errorMessage } from '@/domains/api';
import { authApi } from '../api';
import { useTestInbox } from '../mail-delivery';
import type { IIdpInvitationRequest, IInvitationRequestRouteState } from '../types';
import { AuthShell } from './auth-shell';
import { DoorBackLink } from './door-back-link';
import { RouteNote } from './login-page';
import { TestInboxField, TestInboxNote } from './test-inbox-field';

/** Required and nothing else: the shape of an address is survey-api's to judge, and it answers
 *  a string that is not one plain address 202 like any other. */
const schema = z.object({
  email: z.string().trim().min(1, 'Email is required')
});

type InvitationRequestValues = z.infer<typeof schema>;

/**
 * `/sign-in/request` — asking for an invitation to sign in at Invent's IdP (the IdP sign-in
 * design, decision 7): the way in for somebody whose account signs in nowhere yet, a migrated
 * advisor's above all. `/password/forgot` and `/password/reset` lead here, saying passwords are
 * retired (`legacyRoutes`). Rejected: sending the browser to the IdP's own pages instead — a
 * person with no invitation has nothing there to sign in with.
 *
 * Framed by `AuthShell`, the front door, like every screen reached before a portal's shell.
 */
export function InvitationRequestPage() {
  const routeState = useLocation().state as Nullable<IInvitationRequestRouteState>;

  return (
    <AuthShell title="Ask for an invitation">
      <h1 className="text-2xl text-[var(--color-royal-blue)]">Ask for an invitation</h1>
      <p className="mt-2 mb-6 text-sm text-muted-foreground">
        Veritant’s portals sign you in at Invent. Tell us the email address of your account, and
        an invitation to sign in goes to it.
      </p>

      {routeState?.passwordsRetired === true && (
        <RouteNote>
          Passwords are retired: you sign in at Invent now. If you cannot sign in yet, ask for an
          invitation here.
        </RouteNote>
      )}

      <InvitationRequestForm />
      <DoorBackLink isAuth={false} />
    </AuthShell>
  );
}

/**
 * The request itself — `POST /auth/idp-invitations/requests` — on `/sign-in/request`, and on the
 * sign-in's "not linked" screen filled in with the address Invent signed in.
 *
 * **The confirmation claims nothing it was not told.** survey-api answers 202 for every address,
 * and its worker decides what happens: an unknown, disabled or already linked account gets
 * nothing. So the confirmation says an invitation is on its way *if* the address is an account's
 * that does not sign in yet, and never "we have sent you one". For the same reason nothing is
 * refused here but the test inbox, judged on the request's own strings (`useTestInbox`); what
 * survey-api refuses is said in its words.
 *
 * **A second request is safe**: a live invitation at the same address is sent again rather than
 * doubled, and past survey-api's limit a request sends nothing and the last invitation stays good.
 * That is why "ask again" is the advice for an answer that did not come back.
 */
export function InvitationRequestForm({ email = '' }: { email?: string }) {
  const inbox = useTestInbox();
  const fieldId = React.useId();
  // What the confirmation says it was asked, kept from the moment the 202 came back.
  const [sent, setSent] = React.useState<Nullable<IIdpInvitationRequest>>(null);
  const [formError, setFormError] = React.useState<Nullable<string>>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<InvitationRequestValues>({
    resolver: zodResolver(schema),
    defaultValues: { email }
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    inbox.begin();
    const request: IIdpInvitationRequest = { email: values.email, ...inbox.body };

    try {
      await authApi.requestIdpInvitation(request);
      setSent(request);
    } catch (error) {
      if (inbox.refused(error, values.email)) return;
      // A definite refusal — a 429, say — in survey-api's words: asking again at once would meet
      // it again.
      if (error instanceof ApiError && error.status < 500) {
        setFormError(`The system refused this: ${errorMessage(error, 'no reason given')}`);
        return;
      }
      // An answer that never arrived does not prove the request failed: survey-api may have
      // queued it. Asking again costs nothing (the form's JSDoc).
      console.warn('[Auth] The invitation request was not confirmed:', error);
      setFormError('The request could not be confirmed. Send it again.');
    }
  });

  if (sent) {
    return (
      <div role="status" className="grid gap-3 text-sm text-muted-foreground">
        <p>
          If <strong className="break-words">{sent.email}</strong> is the email address of an
          account that does not sign in yet, an invitation to sign in at Invent is on its way.
        </p>
        {sent.deliverTo && (
          <TestInboxNote>
            this environment sends it to <strong className="break-words">{sent.deliverTo}</strong>{' '}
            instead.
          </TestInboxNote>
        )}
        <p>Nothing after a few minutes? Check your spam folder, or ask again.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="grid gap-5">
      <SimpleFormField
        label="Email"
        htmlFor={`${fieldId}-email`}
        required
        error={errors.email?.message}
      >
        <Input
          id={`${fieldId}-email`}
          // An address shaped as a control and judged by nobody here: the `@`-bearing keyboard,
          // and nothing a phone might capitalise or correct.
          type="text"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoComplete="email"
          placeholder="you@firm.com"
          aria-invalid={!!errors.email}
          {...register('email')}
        />
      </SimpleFormField>

      <TestInboxField
        inbox={inbox}
        id={`${fieldId}-deliver-to`}
        intended={watch('email')}
        disabled={isSubmitting}
      />

      {formError && (
        <p role="alert" className="text-sm font-medium text-destructive">
          {formError}
        </p>
      )}

      <Button type="submit" disabled={isSubmitting || !inbox.settled} className="w-full">
        {isSubmitting ? 'Sending…' : 'Send me an invitation'}
      </Button>
    </form>
  );
}
